/*
 * Loven cart extras
 * - <cart-reserve-timer>: session-persisted countdown that loops when it runs out.
 * - <cart-shipping-bar>: free shipping progress bar. Values are rendered by Liquid;
 *   this only adjusts them when the shopper browses in a converted currency.
 *
 * Both elements live inside markup that Dawn swaps out after every cart change
 * (cart-drawer-items / .drawer__inner / #CartDrawer / cart footer .js-contents),
 * so each fresh copy simply initialises itself in connectedCallback.
 */
(() => {
  const TIMER_STORAGE_KEY = 'loven:cart-reserve-timer';

  const storage = {
    read() {
      try {
        return JSON.parse(window.sessionStorage.getItem(TIMER_STORAGE_KEY)) || null;
      } catch (error) {
        return null;
      }
    },
    write(value) {
      try {
        window.sessionStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(value));
      } catch (error) {
        /* storage unavailable (private mode etc.) - timer still runs for this page */
      }
    },
  };

  const pad = (value) => String(value).padStart(2, '0');

  if (!customElements.get('cart-reserve-timer')) {
    class CartReserveTimer extends HTMLElement {
      connectedCallback() {
        this.clock = this.querySelector('[data-clock]');
        this.durationMs = Math.max(1, parseInt(this.dataset.minutes, 10) || 10) * 60 * 1000;
        this.deadline = this.resolveDeadline();
        this.tick();
        this.interval = window.setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        window.clearInterval(this.interval);
      }

      resolveDeadline() {
        const saved = storage.read();
        const now = Date.now();
        if (saved && saved.duration === this.durationMs && saved.deadline > now) return saved.deadline;
        return this.restart(now);
      }

      restart(now = Date.now()) {
        const deadline = now + this.durationMs;
        storage.write({ deadline, duration: this.durationMs });
        return deadline;
      }

      tick() {
        let remaining = this.deadline - Date.now();
        if (remaining <= 0) {
          this.deadline = this.restart();
          remaining = this.durationMs;
        }
        const totalSeconds = Math.ceil(remaining / 1000);
        if (this.clock) this.clock.textContent = `${pad(Math.floor(totalSeconds / 60))}:${pad(totalSeconds % 60)}`;
      }
    }

    customElements.define('cart-reserve-timer', CartReserveTimer);
  }

  /*
   * Builds a formatter from a Liquid-rendered sample ({{ 100000 | money }} = 1000 units),
   * so converted amounts keep the store's own symbol, separators and decimals.
   */
  const createMoneyFormatter = (rawSample) => {
    // Money formats may contain markup/entities; work with the plain text.
    const sample = rawSample ? new DOMParser().parseFromString(rawSample, 'text/html').body.textContent.trim() : '';
    const match = sample && sample.match(/\d(?:[\d.,'\s  ]*\d)?/);
    if (!match) return null;

    const numberPart = match[0];
    const decimalMatch = numberPart.match(/([.,])(\d{2})$/);
    const decimals = decimalMatch && numberPart.replace(/\D/g, '').length === 6 ? 2 : 0;
    const decimalSeparator = decimals ? decimalMatch[1] : '';
    const integerPart = decimals ? numberPart.slice(0, -3) : numberPart;
    const groupMatch = integerPart.match(/^\d([^\d])\d{3}$/);
    const groupSeparator = groupMatch ? groupMatch[1] : '';

    return (cents) => {
      const units = Math.max(0, cents) / 100;
      const [whole, fraction] = units.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);
      const formatted = decimals ? `${grouped}${decimalSeparator}${fraction}` : grouped;
      return sample.replace(numberPart, formatted);
    };
  };

  if (!customElements.get('cart-shipping-bar')) {
    class CartShippingBar extends HTMLElement {
      connectedCallback() {
        const rate = parseFloat(window.Shopify?.currency?.rate);
        // Liquid already rendered the correct values for the store's main currency.
        if (!rate || rate === 1) return;
        this.applyRate(rate);
      }

      applyRate(rate) {
        const baseThreshold = parseInt(this.dataset.threshold, 10);
        const total = parseInt(this.dataset.total, 10) || 0;
        if (!baseThreshold) return;

        const threshold = Math.round(baseThreshold * rate);
        const remaining = Math.ceil(threshold - total);
        const reached = remaining <= 0;
        const percent = reached ? 100 : Math.min(100, Math.max(0, (total / threshold) * 100));

        this.classList.toggle('is-reached', reached);

        const track = this.querySelector('[data-track]');
        if (track) {
          track.style.setProperty('--cx-progress', `${percent.toFixed(1)}%`);
          track.setAttribute('aria-valuenow', Math.round(percent));
        }

        const amount = this.querySelector('[data-amount]');
        const format = createMoneyFormatter(this.dataset.moneySample);
        if (amount && format && !reached) amount.textContent = format(remaining);
      }
    }

    customElements.define('cart-shipping-bar', CartShippingBar);
  }
})();
